import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Gift, ArrowRight, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact referral summary shown on the vendor/organizer dashboard. Fetches
 * `/referrals/me` once, shows the user's code + share link, invited/converted
 * counts, and a "manage" link that opens the full referrals page.
 */
export default function ReferralsCard({ variant = "vendor" }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        if (!cancelled) setData(data);
      } catch { /* silent — card just hides on failure */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };

  const detailHref = variant === "organizer" ? "/organizer/referrals" : "/vendor/referrals";

  return (
    <div
      data-testid="referrals-card"
      className="mt-8 card-warm rounded-sm p-6"
      style={{
        background: "linear-gradient(135deg, rgba(201,162,75,0.14), rgba(201,162,75,0.03))",
        borderLeft: "3px solid var(--gold)",
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--gold)" }}>
            <Gift className="h-3 w-3" /> Refer a maker
          </div>
          <div className="font-serif text-2xl mt-1.5">Give a free month, get a free month.</div>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Share your code — when a friend upgrades to Maker Pro, you both get $19 credited to your next invoice.
          </p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <code
              data-testid="referrals-card-code"
              className="text-sm px-3 py-1.5 rounded-sm font-mono tracking-wide"
              style={{ backgroundColor: "rgba(31,93,52,0.10)", color: "var(--forest)" }}
            >
              {data.code}
            </code>
            <button
              type="button"
              onClick={copy}
              data-testid="referrals-card-copy"
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
              style={{ borderColor: "rgba(0,0,0,0.15)" }}
            >
              {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy link</>}
            </button>
            <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
              {data.stats.invited} invited · {data.stats.converted} joined Pro · {data.stats.months_earned} free months earned
            </span>
          </div>
        </div>
        <Link
          to={detailHref}
          data-testid="referrals-card-cta"
          className="btn-outline px-5 py-2.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap"
        >
          Manage referrals <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
